import { reactive, ref } from 'vue';
import { api } from './api.ts';

/**
 * What this account can actually do, answered by the API rather than read from
 * a token claim. The shape mirrors the four roles in Part C; each flag is the
 * result of an entitlements check the server made, never a claim we trusted.
 */
export interface Capabilities {
  isApplicant: boolean;
  judgePanelIds: string[];
  isConductor: boolean;
  isTeam: boolean;
}

export const session = reactive<{
  loaded: boolean;
  error: string | null;
  capabilities: Capabilities;
  /** Whether anyone is signed in. Written by App.vue, which can observe it. */
  isAuthenticated: boolean;
  /** False until Logto has finished working that out. */
  authReady: boolean;
}>({
  loaded: false,
  error: null,
  capabilities: { isApplicant: false, judgePanelIds: [], isConductor: false, isTeam: false },
  isAuthenticated: false,
  authReady: false,
});

/**
 * Records the auth state once it is known.
 *
 * The router guard does not wait for this: on a fresh load of a protected URL
 * it runs before any component mounts, so it asks Logto directly instead. This
 * is for the nav and the views, which mount later.
 */
/**
 * Set when the API refuses something because the token lacks a scope.
 *
 * A permission that was granted after somebody signed in is not in the token
 * they are holding, and nothing about "Requires scope auditions:review" tells
 * a conductor that the fix is to sign in again. It happened to the first
 * person invited to judge: the role was attached a second after her token was
 * minted, and the app simply refused her for a day.
 *
 * Signing in again is the whole fix, so the shell offers it. Not automatic: a
 * redirect on every refusal would loop for somebody who genuinely does not
 * have the role.
 */
export const accessChanged = ref(false);

export function markAuthReady(isAuthenticated: boolean): void {
  session.isAuthenticated = isAuthenticated;
  session.authReady = true;
}



/**
 * One capabilities request at a time, however many things ask for one.
 *
 * This is not a nicety. Asking for one needs an access token; asking the Logto
 * SDK for a token increments its loading counter and decrements it again, and
 * `isLoading` is one of the things App.vue watches to decide whether to load
 * capabilities. So a request that has not come back yet — a page reload, where
 * the token has to be refreshed against Logto first — spawns another on every
 * flip of that counter, each of which spawns more.
 *
 * The result was not a slow page: it was a tab that stopped responding within
 * a second of a refresh and was killed by the browser a few minutes later, out
 * of memory. The guard it had, `session.loaded`, is only true once a request
 * has *finished*, which is exactly the window the storm lives in.
 */
let inFlight: Promise<void> | null = null;

export function loadCapabilities(): Promise<void> {
  inFlight ??= read().finally(() => { inFlight = null; });
  return inFlight;
}

async function read(): Promise<void> {
  try {
    session.capabilities = await api<Capabilities>('/v1/me/capabilities');
    session.error = null;
  } catch {
    // Fail closed: an account whose capabilities cannot be read sees nothing
    // rather than everything.
    session.capabilities = { isApplicant: false, judgePanelIds: [], isConductor: false, isTeam: false };
    session.error = 'We could not check what your account can open.';
  } finally {
    session.loaded = true;
  }
}

/** Where a signed-in person lands, by what they can do rather than who they are. */
export function landingRoute(): string {
  const c = session.capabilities;
  if (c.isTeam) return '/team/master';
  if (c.isConductor) return '/review';
  if (c.judgePanelIds.length) return '/judge';
  return '/my/applications';
}
